import { useFormikContext } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { dollarsToCents } from "@/features/worksheets/money";

type SaveState = "idle" | "saving" | "saved" | "error";

interface AutosaveFormProps {
  /** PATCH the changed fields (camelCase keys, money already in cents). */
  save: (patch: Record<string, unknown>) => Promise<unknown>;
  /** Field names whose Formik value is a dollar string → converted to cents. */
  moneyFields?: readonly string[];
  /** Debounce window in ms (default 600). */
  delay?: number;
}

/**
 * Renders inside a `<Formik>` and autosaves changed fields as the user edits.
 * Diffs the current values against the last-saved snapshot on a debounce and
 * PATCHes only what changed — the spec's "autosave-per-field" behavior without
 * wiring each input by hand. Money fields (dollar strings) are converted to
 * integer cents before sending.
 */
export function AutosaveForm({ save, moneyFields = [], delay = 600 }: AutosaveFormProps) {
  const { values } = useFormikContext<Record<string, unknown>>();
  const money = useRef(new Set(moneyFields));
  const lastSaved = useRef<Record<string, unknown>>({ ...values });
  const valuesRef = useRef(values);
  valuesRef.current = values;
  // Serialize saves: only one PATCH is ever in flight, and `lastSaved` is
  // advanced to exactly the snapshot that was persisted — so a save that
  // resolves can't clobber edits made while it was in flight.
  const saving = useRef(false);
  const mounted = useRef(true);
  const [state, setState] = useState<SaveState>("idle");

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  const buildPatch = () => {
    const patch: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(valuesRef.current)) {
      if (value !== lastSaved.current[key]) {
        patch[key] = money.current.has(key) ? dollarsToCents(value as string) : value;
      }
    }
    return patch;
  };

  const runSave = useCallback(async () => {
    if (saving.current) return; // a save is in flight; it re-checks when it finishes
    const snapshot = { ...valuesRef.current };
    const patch = buildPatch();
    if (Object.keys(patch).length === 0) return;

    saving.current = true;
    setState("saving");
    try {
      await save(patch);
      lastSaved.current = snapshot;
      if (mounted.current) setState("saved");
    } catch {
      if (mounted.current) setState("error");
      toast.error("Couldn't autosave — check your entries.");
    } finally {
      saving.current = false;
    }
    // Coalesce any edits made while the save was in flight.
    if (mounted.current && Object.keys(buildPatch()).length > 0) void runSave();
  }, [save]);

  useEffect(() => {
    const handle = setTimeout(() => void runSave(), delay);
    return () => clearTimeout(handle);
  }, [values, runSave, delay]);

  return (
    <p className="text-muted-foreground h-4 text-right text-xs" aria-live="polite">
      {state === "saving" && "Saving…"}
      {state === "saved" && "All changes saved"}
      {state === "error" && <span className="text-destructive">Save failed</span>}
    </p>
  );
}
